// FE-SCRM-020 — Benefits Intelligence panel.
//
// Surfaces Form 5500-derived insights on the Prospect detail page
// (and, in a future chunk, the Opportunity detail page). The panel
// is pure-render: parent loads the data and passes it in. v1 ships
// with mocked data via :func:`mockBenefitsIntelligence`; the
// real-API path lands when a Prospect detail page exists to host it
// (a separate FE chunk).
//
// Vocabulary discipline: every label is producer-flavoured. We
// don't say ``ACK_ID`` / ``Schedule A`` / ``filing_year`` in primary
// copy — "Filed 2024 on time" beats "Filing status: Processed
// (year=2024)". Power-user surface is one click away via the
// "View raw filing details" link.

import { useMemo } from "react";

import {
  labelForAlertType,
  labelForFitReason,
} from "./labels";
import type {
  AlertSeverity,
  BenefitsAlert,
  BenefitsIntelligence,
  CarrierLineup,
} from "./types";


type Props = {
  data: BenefitsIntelligence | null;
  isLoading?: boolean;
  /** Optional override label shown above the panel. */
  title?: string;
  /** FE-SCRM-062 — when wired, the empty state surfaces a "Link
   *  Form 5500 sponsor" CTA. Also flips the panel into empty-state
   *  rendering when ``data.fitScore === null`` (the BE returns the
   *  shaped-but-empty payload for prospects with no sponsor link).
   *  Without this prop the panel renders the populated branch
   *  with "—" placeholders, preserving the legacy contract. */
  onLinkSponsor?: () => void;
  /** FE-SCRM-062 — when wired and the panel is in the populated
   *  branch, surfaces a muted "Unlink" affordance in the header so
   *  a producer who realises auto-match was wrong can undo it. */
  onUnlinkSponsor?: () => void;
};


/**
 * Renders the Benefits Intelligence card. ``data === null`` with
 * ``isLoading=true`` shows a skeleton; ``data === null`` with
 * ``isLoading=false`` shows the empty state.
 */
export function BenefitsIntelligencePanel({
  data, isLoading = false, title = "Benefits intelligence",
  onLinkSponsor, onUnlinkSponsor,
}: Props) {
  if (isLoading) {
    return (
      <Container title={title}>
        <Skeleton />
      </Container>
    );
  }

  // The BE returns a shaped-but-empty payload for prospects with no
  // Form 5500 link. When the host wires the link CTA we treat that
  // empty payload as the empty state so the producer sees the
  // linking action instead of a wall of "—" placeholders.
  const isEmpty = data === null
    || (Boolean(onLinkSponsor) && data.fitScore === null);

  if (isEmpty) {
    return (
      <Container title={title}>
        <EmptyState onLinkSponsor={onLinkSponsor} />
      </Container>
    );
  }

  return (
    <Container title={title} onUnlinkSponsor={onUnlinkSponsor}>
      <div className="flex flex-col gap-5">
        <HeadlineRow data={data} />
        <ReasonChips reasons={data.fitReasons} />
        <Divider />
        <ParticipantTrend
          current={data.participants.current}
          prior={data.participants.prior}
          delta={data.participants.delta}
        />
        <PlanFootprint
          count={data.plans.count}
          names={data.plans.names}
        />
        <RenewalTiming
          renewalMonth={data.renewalMonth}
          renewalYear={data.renewalYear}
          planYearEnd={data.planYearEnd}
        />
        <CarrierLineupView carriers={data.carriers} />
        {data.scheduleC.visible ? (
          <ScheduleCBlurb count={data.scheduleC.providerCount} />
        ) : null}
        <ActiveAlerts alerts={data.alerts} />
        {data.rawFilingHref ? (
          <FilingDrillDownLink href={data.rawFilingHref} />
        ) : null}
      </div>
    </Container>
  );
}


// ── Shell ──────────────────────────────────────────────────────────────


function Container({
  title, children, onUnlinkSponsor,
}: {
  title: string;
  children: React.ReactNode;
  onUnlinkSponsor?: () => void;
}) {
  return (
    <section
      data-testid="benefits-intelligence-panel"
      className={[
        "rounded-lg border border-border-subtle bg-raised p-5",
      ].join(" ")}
      aria-labelledby="benefits-intel-heading"
    >
      <header className="mb-4 flex items-baseline justify-between gap-2">
        <h2
          id="benefits-intel-heading"
          className="text-sm font-semibold tracking-tight text-text-primary"
        >
          {title}
        </h2>
        <div className="flex items-baseline gap-3">
          {onUnlinkSponsor && (
            <button
              type="button"
              data-testid="bi-unlink-form5500-link"
              onClick={onUnlinkSponsor}
              className={[
                "text-[11px] text-text-tertiary underline-offset-2",
                "hover:text-text-secondary hover:underline",
              ].join(" ")}
            >
              Unlink Form 5500 sponsor
            </button>
          )}
          <span
            className={[
              "rounded-full bg-recessed px-2 py-0.5",
              "text-[10px] uppercase tracking-wide text-text-tertiary",
            ].join(" ")}
          >
            From DOL Form 5500
          </span>
        </div>
      </header>
      {children}
    </section>
  );
}


function Divider() {
  return <div className="h-px bg-border-subtle" />;
}


// ── Headline (fit score + tooltip) ────────────────────────────────────


function HeadlineRow({ data }: { data: BenefitsIntelligence }) {
  const score = data.fitScore;
  const band = useMemo(() => fitScoreBand(score), [score]);

  return (
    <div className="flex items-center gap-4" data-testid="bi-headline">
      <FitScoreBadge score={score} band={band} />
      <div className="flex flex-col">
        <span className="text-xs uppercase tracking-wide text-text-tertiary">
          Fit score
        </span>
        <span className="text-sm text-text-secondary">
          {band.copy}
        </span>
      </div>
    </div>
  );
}


function FitScoreBadge({
  score, band,
}: {
  score: number | null;
  band: { tone: string; copy: string };
}) {
  if (score === null) {
    return (
      <div
        data-testid="bi-fit-score"
        className={[
          "flex h-16 w-16 items-center justify-center rounded-full",
          "border border-dashed border-border-subtle text-text-tertiary",
        ].join(" ")}
        aria-label="Fit score not yet computed"
        title="No Form 5500 data linked yet"
      >
        <span className="text-xs">—</span>
      </div>
    );
  }
  return (
    <div
      data-testid="bi-fit-score"
      className={[
        "flex h-16 w-16 items-center justify-center rounded-full",
        band.tone,
      ].join(" ")}
      aria-label={`Fit score ${score} out of 100`}
      title={`${score}/100 — ${band.copy}`}
    >
      <span className="text-xl font-semibold tabular-nums">{score}</span>
    </div>
  );
}


function fitScoreBand(score: number | null) {
  if (score === null) {
    return { tone: "bg-recessed text-text-tertiary", copy: "Not yet computed" };
  }
  if (score >= 75) {
    return {
      tone: "bg-teal-soft text-teal-strong",
      copy: "Strong fit — prioritise outreach",
    };
  }
  if (score >= 55) {
    return {
      tone: "bg-amber-soft text-amber-strong",
      copy: "Promising fit — worth a touchpoint",
    };
  }
  if (score >= 35) {
    return {
      tone: "bg-recessed text-text-secondary",
      copy: "Neutral — qualify before committing",
    };
  }
  return {
    tone: "bg-rose-soft text-rose-strong",
    copy: "Low fit — handle reactively",
  };
}


// ── Chips ──────────────────────────────────────────────────────────────


function ReasonChips({ reasons }: { reasons: string[] }) {
  if (reasons.length === 0) {
    return (
      <p className="text-xs text-text-tertiary" data-testid="bi-reasons-empty">
        No standout signals on this prospect yet.
      </p>
    );
  }
  return (
    <ul
      data-testid="bi-reasons"
      className="flex flex-wrap gap-1.5"
    >
      {reasons.map((code) => (
        <li
          key={code}
          className={[
            "rounded-full bg-recessed px-2.5 py-1",
            "text-[11px] font-medium text-text-secondary",
          ].join(" ")}
          data-chip={code}
        >
          {labelForFitReason(code)}
        </li>
      ))}
    </ul>
  );
}


// ── Participant trend ──────────────────────────────────────────────────


function ParticipantTrend({
  current, prior, delta,
}: {
  current: number | null;
  prior: number | null;
  delta: number | null;
}) {
  if (current === null) {
    return (
      <Stat
        label="Active participants"
        primary={<span className="text-text-tertiary">No filing yet</span>}
      />
    );
  }
  const pct =
    prior && prior > 0
      ? Math.round(((delta ?? 0) / prior) * 1000) / 10
      : null;
  const trend =
    delta === null
      ? null
      : delta > 0
        ? `+${delta.toLocaleString()}${pct !== null ? ` (+${pct}%)` : ""}`
        : delta < 0
          ? `${delta.toLocaleString()}${pct !== null ? ` (${pct}%)` : ""}`
          : "Flat";
  return (
    <Stat
      label="Active participants"
      primary={current.toLocaleString()}
      secondary={
        prior !== null && trend
          ? `${trend} vs. prior year (${prior.toLocaleString()})`
          : prior !== null
            ? `Prior year: ${prior.toLocaleString()}`
            : "First filing on record"
      }
      testid="bi-participants"
    />
  );
}


// ── Plans ──────────────────────────────────────────────────────────────


function PlanFootprint({
  count, names,
}: {
  count: number;
  names: string[];
}) {
  if (count === 0) {
    return (
      <Stat
        label="Plans on file"
        primary={<span className="text-text-tertiary">No plans on record</span>}
      />
    );
  }
  return (
    <Stat
      label={`Plans on file (${count})`}
      primary={
        <ul className="flex flex-col gap-0.5" data-testid="bi-plans">
          {names.slice(0, 6).map((n) => (
            <li
              key={n}
              className="truncate text-sm text-text-primary"
              title={n}
            >
              {n}
            </li>
          ))}
          {names.length > 6 ? (
            <li className="text-xs text-text-tertiary">
              + {names.length - 6} more
            </li>
          ) : null}
        </ul>
      }
    />
  );
}


// ── Renewal timing ────────────────────────────────────────────────────


function RenewalTiming({
  renewalMonth, renewalYear, planYearEnd,
}: {
  renewalMonth: number | null;
  renewalYear: number | null;
  planYearEnd: string | null;
}) {
  if (renewalMonth === null || renewalYear === null) {
    return (
      <Stat
        label="Renewal"
        primary={<span className="text-text-tertiary">Unknown</span>}
        secondary={planYearEnd ? `Plan year ends ${planYearEnd}` : undefined}
      />
    );
  }
  const monthName = new Date(2000, renewalMonth - 1).toLocaleString(
    "default", { month: "long" },
  );
  return (
    <Stat
      label="Renewal"
      primary={`${monthName} ${renewalYear}`}
      secondary={planYearEnd ? `Plan year ends ${planYearEnd}` : undefined}
      testid="bi-renewal"
    />
  );
}


// ── Carriers ───────────────────────────────────────────────────────────


const CARRIER_LINE_ORDER: Array<keyof CarrierLineup> = [
  "medical", "dental", "vision", "life", "other",
];

const CARRIER_LINE_LABELS: Record<keyof CarrierLineup, string> = {
  medical: "Medical",
  dental: "Dental",
  vision: "Vision",
  life: "Life & disability",
  other: "Other",
};


function CarrierLineupView({ carriers }: { carriers: CarrierLineup }) {
  const populated = CARRIER_LINE_ORDER.filter(
    (key) => carriers[key].length > 0,
  );
  if (populated.length === 0) {
    return (
      <Stat
        label="Known carriers"
        primary={
          <span className="text-text-tertiary">
            No carrier signal on Schedule A
          </span>
        }
      />
    );
  }
  return (
    <Stat
      label="Known carriers"
      primary={
        <dl
          className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm"
          data-testid="bi-carriers"
        >
          {populated.map((key) => (
            <CarrierRow
              key={key}
              label={CARRIER_LINE_LABELS[key]}
              names={carriers[key]}
            />
          ))}
        </dl>
      }
    />
  );
}


function CarrierRow({ label, names }: { label: string; names: string[] }) {
  return (
    <>
      <dt className="text-xs uppercase tracking-wide text-text-tertiary">
        {label}
      </dt>
      <dd className="text-sm text-text-primary">{names.join(", ")}</dd>
    </>
  );
}


function ScheduleCBlurb({ count }: { count: number }) {
  return (
    <p
      className="text-xs text-text-secondary"
      data-testid="bi-schedule-c"
    >
      <strong className="font-semibold text-text-primary">
        Compensation visibility
      </strong>
      {": "}
      {count} service-provider record{count === 1 ? "" : "s"} disclosed
      on the latest filing.
    </p>
  );
}


// ── Active alerts ──────────────────────────────────────────────────────


function ActiveAlerts({ alerts }: { alerts: BenefitsAlert[] }) {
  if (alerts.length === 0) {
    return null;
  }
  return (
    <div className="flex flex-col gap-2" data-testid="bi-alerts">
      <h3 className="text-xs uppercase tracking-wide text-text-tertiary">
        Active alerts ({alerts.length})
      </h3>
      <ul className="flex flex-col gap-1.5">
        {alerts.map((a) => (
          <AlertRow key={a.id} alert={a} />
        ))}
      </ul>
    </div>
  );
}


function AlertRow({ alert }: { alert: BenefitsAlert }) {
  const tone = severityTone(alert.severity);
  return (
    <li
      data-alert-type={alert.alertType}
      className={[
        "flex items-start gap-2 rounded-md border px-2.5 py-1.5",
        tone.container,
      ].join(" ")}
    >
      <span
        className={[
          "mt-0.5 rounded-sm px-1 text-[9px] font-semibold uppercase",
          "tracking-wider",
          tone.badge,
        ].join(" ")}
      >
        {alert.severity}
      </span>
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-medium text-text-primary">
          {labelForAlertType(alert.alertType)}
        </span>
        <span className="text-xs text-text-secondary">
          {alert.summary}
        </span>
      </div>
    </li>
  );
}


function severityTone(severity: AlertSeverity) {
  if (severity === "urgent") {
    return {
      container: "border-rose-soft bg-rose-soft/40",
      badge: "bg-rose-strong text-white",
    };
  }
  if (severity === "signal") {
    return {
      container: "border-amber-soft bg-amber-soft/40",
      badge: "bg-amber-strong text-white",
    };
  }
  return {
    container: "border-border-subtle bg-recessed",
    badge: "bg-recessed text-text-secondary",
  };
}


// ── Drill-down ────────────────────────────────────────────────────────


function FilingDrillDownLink({ href }: { href: string }) {
  return (
    <a
      href={href}
      data-testid="bi-raw-filing-link"
      className={[
        "inline-flex items-center gap-1 text-xs",
        "text-teal-strong hover:underline",
      ].join(" ")}
    >
      View raw filing details →
    </a>
  );
}


// ── Generic helpers ──────────────────────────────────────────────────


function Stat({
  label, primary, secondary, testid,
}: {
  label: string;
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  testid?: string;
}) {
  return (
    <div className="flex flex-col gap-0.5" data-testid={testid}>
      <span className="text-xs uppercase tracking-wide text-text-tertiary">
        {label}
      </span>
      <span className="text-sm font-medium text-text-primary">
        {primary}
      </span>
      {secondary ? (
        <span className="text-xs text-text-secondary">{secondary}</span>
      ) : null}
    </div>
  );
}


function Skeleton() {
  return (
    <div
      data-testid="bi-skeleton"
      className="flex flex-col gap-4 animate-pulse"
    >
      <div className="flex items-center gap-4">
        <div className="h-16 w-16 rounded-full bg-recessed" />
        <div className="flex flex-col gap-2">
          <div className="h-3 w-24 rounded bg-recessed" />
          <div className="h-3 w-40 rounded bg-recessed" />
        </div>
      </div>
      <div className="flex gap-2">
        <div className="h-5 w-20 rounded-full bg-recessed" />
        <div className="h-5 w-24 rounded-full bg-recessed" />
        <div className="h-5 w-16 rounded-full bg-recessed" />
      </div>
      <div className="h-12 rounded bg-recessed" />
      <div className="h-12 rounded bg-recessed" />
    </div>
  );
}


function EmptyState({ onLinkSponsor }: { onLinkSponsor?: () => void }) {
  return (
    <div
      data-testid="bi-empty"
      className="flex flex-col items-center gap-3 py-6 text-center"
    >
      <p className="text-sm text-text-secondary">
        Link this prospect to a Form 5500 sponsor to unlock benefits
        intelligence.
      </p>
      <p className="text-xs text-text-tertiary">
        Fit score, carrier lineup, and change alerts appear here once
        the prospect is matched.
      </p>
      {onLinkSponsor && (
        <button
          type="button"
          data-testid="bi-link-form5500-cta"
          onClick={onLinkSponsor}
          className={[
            "mt-1 rounded-md bg-teal px-3 py-1.5 text-sm font-medium",
            "text-text-inverse hover:opacity-90",
          ].join(" ")}
        >
          Link Form 5500 sponsor
        </button>
      )}
    </div>
  );
}
